/**
 * Shared department dashboard (WS-F, workspace docs/guardian/06, 2026-10-23):
 * at the top of the department page. Every member sees the watched-area count
 * and the most recent team alerts; admins additionally see usage (answers this
 * month, ratings, active members) and knowledge status (documents, approved
 * rules, live version). The Backend omits the usage/knowledge blocks entirely
 * for members, so a member response can never hint at them. All text comes from
 * the Backend and is rendered as text only. A 503 `org_unavailable` hides the
 * section (like DepartmentRules on rules_unavailable).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import { fetchDepartmentDashboard } from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';
import { alertSourceLabel, formatDeptTime } from '../utils/departmentOrg';

function Metric({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="h6" component="p">{value}</Typography>
    </Box>
  );
}

/**
 * @param {object} props
 * @param {string|null} props.authToken
 */
export default function DepartmentDashboard({ authToken }) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [off, setOff] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    if (!authToken) return;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    const res = await fetchDepartmentDashboard(authToken);
    if (!alive.current) return;
    if (res?.success) {
      setState({ status: 'ready', data: res.data || {}, error: null });
    } else if (res?.data?.code === 'org_unavailable') {
      setOff(true);
    } else {
      setState({ status: 'error', data: null, error: departmentErrorMessage(res, 'Could not load the dashboard.') });
    }
  }, [authToken]);
  useEffect(() => { load(); }, [load]);

  if (off) return null;

  const { status, data, error } = state;
  const areas = Array.isArray(data?.areas) ? data.areas : [];
  const recent = Array.isArray(data?.recentAlerts) ? data.recentAlerts : [];
  const usage = data?.usage;
  const knowledge = data?.knowledge;

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="h6" component="h2">Dashboard</Typography>

      {status === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} role="status">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading the dashboard…</Typography>
        </Box>
      )}
      {status === 'error' && <Alert severity="error">{error}</Alert>}

      {status === 'ready' && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
            <Metric label="Watched areas" value={areas.length} />
            <Metric label="Recent alerts" value={recent.length} />
            {usage && <Metric label="Answers this month" value={usage.answersThisMonth ?? 0} />}
            {usage && (
              <Metric
                label="Ratings this month"
                value={`${usage.ratedThisMonth ?? 0}${usage.averageRating != null ? ` (avg ${usage.averageRating})` : ''}`}
              />
            )}
            {usage && <Metric label="Active members" value={usage.activeMembers ?? 0} />}
            {knowledge && <Metric label="Documents" value={knowledge.documents ?? 0} />}
            {knowledge && <Metric label="Approved rules" value={knowledge.approvedRules ?? 0} />}
            {knowledge && (
              <Metric label="Live knowledge version" value={knowledge.liveVersion != null ? `v${knowledge.liveVersion}` : 'None published'} />
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Latest alerts</Typography>
            {recent.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No recent alerts for your watched areas.</Typography>
            ) : (
              <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {recent.slice(0, 5).map((a) => (
                  <Box component="li" key={a.id} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'baseline' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.event}</Typography>
                    <Typography variant="caption" color="text.secondary">{alertSourceLabel(a.source)}</Typography>
                    {formatDeptTime(a.detectedAt) && (
                      <Typography variant="caption" color="text.secondary">· {formatDeptTime(a.detectedAt)}</Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
}
